import {useEffect, useState} from "react";
import {Helmet} from "react-helmet-async";
import {siteName, siteUrl} from "./Seo";
import {SiteSettings} from "../sanity/types";
import {fallbackSiteSettings} from "../sanity/fallbacks";
import {getSiteSettings} from "../sanity/queries";

const buildJsonLd = (settings: SiteSettings) => {
  const address = settings.address || fallbackSiteSettings.address;
  const phone = settings.phone || fallbackSiteSettings.phone;
  const email = settings.email || fallbackSiteSettings.email;

  const addressMatch = address
    ? address.match(/^(.*?),\s*([^,]+),\s*([A-Z]{2})\s*(\d{5})$/)
    : null;
  const socialUrls = Object.values(settings.socialLinks || {}).filter(
    (value): value is string =>
      typeof value === "string" && /^https?:\/\//.test(value)
  );
  const addressJson = addressMatch
    ? {
        streetAddress: addressMatch[1],
        addressLocality: addressMatch[2],
        addressRegion: addressMatch[3],
        postalCode: addressMatch[4],
        addressCountry: "US",
      }
    : {streetAddress: address};

  return {
    "@context": "https://schema.org",
    "@type": "Church",
    name: siteName,
    url: siteUrl,
    telephone: phone,
    email,
    address: {
      "@type": "PostalAddress",
      ...addressJson,
    },
    sameAs: socialUrls,
  };
};

export function SiteJsonLd() {
  const [settings, setSettings] =
    useState<SiteSettings>(fallbackSiteSettings);

  useEffect(() => {
    let isMounted = true;
    getSiteSettings().then((data) => {
      if (!isMounted || !data) return;
      setSettings({
        ...fallbackSiteSettings,
        ...data,
      });
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const jsonLd = buildJsonLd(settings);

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
    </Helmet>
  );
}
